import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_LABEL = {
  generating_preview: "Rendering your slideshow tribute (usually 30–90 seconds)…",
  preview_ready: "Your preview is ready. Watch it below — and unlock the watermark-free version for $9.99.",
  paid: "Your tribute video is unlocked. Download the full-quality MP4 below.",
  failed: "We hit a snag generating your video.",
  pending: "Waiting…",
};

export default function TributeVideo() {
  const [params] = useSearchParams();
  const jobId = params.get("job_id");
  const sessionId = params.get("session_id");
  const canceled = params.get("video_canceled");
  const [status, setStatus] = useState("generating_preview");
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoGone, setVideoGone] = useState(false); // file lost in past redeploy
  const pollTimer = useRef(null);
  const finalizedRef = useRef(false);

  useEffect(() => {
    if (!jobId) {
      setError("Missing job ID. Open this page from your obituary detail.");
      return;
    }
    if (canceled) toast.message("Payment canceled. Your preview is still here.");

    const finalize = async () => {
      if (!sessionId || finalizedRef.current) return;
      finalizedRef.current = true;
      try {
        const { data } = await api.post(`/tribute-video/finalize/${sessionId}`);
        if (data?.status) setStatus(data.status);
        if (data?.is_paid) toast.success("Payment confirmed — watermark removed!");
      } catch (e) {
        if (e?.response?.status !== 404) toast.error(e?.response?.data?.detail || "Could not confirm payment.");
      }
    };

    const tick = async () => {
      try {
        const { data } = await api.get(`/tribute-video/status/${jobId}`);
        setJob(data);
        setStatus(data.status);
        if (["preview_ready", "paid", "failed"].includes(data.status)) {
          if (pollTimer.current) clearInterval(pollTimer.current);
        }
      } catch (e) {
        if (e?.response?.status !== 404) setError(e?.response?.data?.detail || "Could not load video status.");
      }
    };

    finalize().then(tick);
    pollTimer.current = setInterval(tick, 8000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [jobId, sessionId, canceled]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/tribute-video/unlock-checkout`, {
        job_id: jobId,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else if (data.already_paid) { toast.success("Already unlocked!"); setStatus("paid"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setUnlocking(false);
    }
  };

  const download = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const res = await api.get(`/tribute-video/download/${jobId}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const isPaid = !!job?.is_paid;
      a.download = `tribute-${(job?.deceased_name || "memorial").replace(/\s+/g, "-").toLowerCase()}${isPaid ? "" : "-preview"}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Downloaded.");
    } catch (e) {
      // 410 Gone = the video file was lost in a previous pod redeploy (pre-GridFS era).
      // Surface a recovery banner instead of a generic "Download failed".
      if (e?.response?.status === 410) {
        setVideoGone(true);
        toast.error("This preview was lost in an earlier redeploy. Please regenerate it from your obituary.");
      } else {
        // axios with responseType=blob returns the error body as a Blob too — try to read it.
        let msg = "Download failed.";
        try {
          if (e?.response?.data instanceof Blob) {
            const txt = await e.response.data.text();
            const parsed = JSON.parse(txt);
            msg = parsed?.detail || msg;
          } else {
            msg = e?.response?.data?.detail || msg;
          }
        } catch (_) { /* ignore parse errors, keep default msg */ }
        toast.error(msg);
      }
    } finally {
      setDownloading(false);
    }
  };

  const isPaid = !!job?.is_paid;
  const isReady = status === "preview_ready" || status === "paid";
  const progressPct = { generating_preview: 50, preview_ready: 100, paid: 100, failed: 100, pending: 10 }[status] || 5;
  const token = localStorage.getItem("or_token") || "";

  return (
    <div style={{ maxWidth: 820, margin: "60px auto", padding: "0 24px" }} data-testid="tribute-video-page">
      <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>TRIBUTE VIDEO</div>
      <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: "0 0 8px", fontWeight: 400 }}>
        {isPaid ? "Your slideshow tribute" : (status === "preview_ready" ? "Preview your slideshow" : "Building your slideshow")}
      </h1>
      <p className="muted" style={{ fontSize: 14, marginBottom: 28 }}>
        A heartfelt slideshow tribute built from your uploaded photos — gentle Ken Burns motion, soft music, dignified pacing.
        {!isPaid && status === "preview_ready" && " Watch the watermarked preview first; unlock the clean version for $9.99 only if you love it."}
      </p>

      {error && (
        <div data-testid="tribute-error" style={{ background: "#2a1212", border: "1px solid #5a2a2a", color: "#f5c5c5", padding: 16, borderRadius: 10, marginBottom: 18 }}>
          {error}
        </div>
      )}

      <div style={{ background: "#0f0f10", border: "1px solid #1f1f1f", borderRadius: 14, padding: 28, marginBottom: 18 }}>
        <div style={{ fontSize: 12, color: "var(--gold)", letterSpacing: "0.2em", marginBottom: 8 }}>STATUS</div>
        <div data-testid="tribute-status-label" className="serif" style={{ fontSize: 20, color: "#fff", marginBottom: 18, lineHeight: 1.5 }}>
          {STATUS_LABEL[status] || "Working…"}
        </div>

        <div style={{ background: "#1a1a1c", borderRadius: 999, height: 8, overflow: "hidden", marginBottom: 22 }}>
          <div
            data-testid="tribute-progress"
            style={{
              width: `${progressPct}%`, height: "100%",
              background: status === "failed" ? "#8a3a3a" : "var(--gold)",
              transition: "width 600ms ease",
            }}
          />
        </div>

        {isReady && !videoGone && (
          <div data-testid="tribute-ready-block">
            <video
              data-testid="tribute-video-player"
              controls
              autoPlay
              loop
              muted
              playsInline
              key={`${jobId}-${isPaid ? "paid" : "preview"}`}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/tribute-video/download/${jobId}?token=${encodeURIComponent(token)}${isPaid ? "" : "&preview=1"}`}
              onError={() => {
                // If the embedded <video> can't load, the underlying URL most likely
                // returned 410 Gone (file lost in pre-GridFS redeploy). Flip to the
                // recovery banner so the user has a clear next step.
                setVideoGone(true);
              }}
              style={{ width: "100%", maxHeight: 460, borderRadius: 10, background: "#000", marginBottom: 18 }}
            />

            {!isPaid && (
              <div style={{ background: "#1a140a", border: "1px solid #3d2f12", borderRadius: 12, padding: 18, marginBottom: 16 }}>
                <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 6 }}>Love it? Unlock the clean version</div>
                <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
                  Pay <b style={{ color: "var(--gold)" }}>$9.99 once</b> and get the watermark-free, full-quality MP4 — yours to download, share, and play at the service.
                </div>
                <button
                  data-testid="tribute-unlock-btn"
                  onClick={unlock}
                  disabled={unlocking}
                  className="btn-gold"
                  style={{ padding: "14px 26px", fontSize: 14 }}
                >
                  {unlocking ? "Opening checkout…" : "Unlock for $9.99 →"}
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button data-testid="tribute-download-btn" onClick={download} disabled={downloading} className={isPaid ? "btn-gold" : "btn-ghost"} style={{ padding: "12px 22px" }}>
                {downloading ? "Downloading…" : (isPaid ? "Download MP4" : "Download watermarked preview")}
              </button>
              {job?.obituary_id && (
                <Link to={`/obituary/${job.obituary_id}`} className="btn-ghost" data-testid="tribute-back-link" style={{ padding: "12px 22px", textDecoration: "none" }}>
                  Back to obituary
                </Link>
              )}
            </div>
          </div>
        )}

        {videoGone && isReady && (
          <div data-testid="tribute-video-gone-block" style={{ background: "#1a140a", border: "1px solid #5a4a18", borderRadius: 12, padding: 22 }}>
            <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>This preview is no longer available</div>
            <p className="muted" style={{ fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
              The video file for this older preview was lost during a previous server
              redeploy (before we moved videos to permanent cloud storage). Good news:
              no charge was made for this preview, and all <b>future</b> previews are
              now stored permanently and safe across redeploys.
            </p>
            <p className="muted" style={{ fontSize: 14, marginBottom: 18 }}>
              Please regenerate the slideshow from your obituary — it only takes 30–90 seconds.
            </p>
            {job?.obituary_id && (
              <Link
                to={`/obituary/${job.obituary_id}`}
                className="btn-gold"
                data-testid="tribute-regenerate-link"
                style={{ display: "inline-block", padding: "12px 22px", textDecoration: "none" }}
              >
                ↻ Go back & regenerate preview
              </Link>
            )}
          </div>
        )}

        {status === "failed" && (
          <div data-testid="tribute-failed-block" style={{ color: "#f5c5c5" }}>
            <p style={{ marginBottom: 10 }}>{job?.error || "The slideshow render hit an error. No charge was made — the preview is free. Please try again or contact support."}</p>
            <p>Job ID for support: <code style={{ color: "#ddd" }}>{jobId}</code></p>
          </div>
        )}

        {status === "generating_preview" && (
          <div className="muted" style={{ fontSize: 12 }}>
            You can safely close this page. The slideshow usually renders in 30–90 seconds — refresh this URL to watch it. No payment is taken until you decide to unlock.
          </div>
        )}
      </div>
    </div>
  );
}
